import { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useSoloArtistSubscription } from "@/hooks/useSoloArtistSubscription";
import { Loader2, CreditCard } from "lucide-react";
import { isNativeIOS } from "@/lib/platform";
import { hasPremiumEntitlement } from "@/lib/revenueCat";
import { IOSPaywall } from "@/components/auth/IOSPaywall";
import { useTranslation } from "react-i18next";
import { useBillingMoney, useLocale } from "@/contexts/LocaleContext";

const SOLO_ARTIST_PRODUCT_IDS = ["prod_UHhj1sT45L9QF3", "prod_U3vJWTq6qgy6oN", "prod_U3vUG04KE6Waf8", "prod_UHhkRGWqPIpsy9", "prod_Ts51t8vc1UM34A"];
const FIRST_STUDIO_PRODUCT_IDS = ["prod_UHhkhcbs1vdGGP", "prod_U3vJh6pGKCkRdV", "prod_U3vU0Fp1Fw3y6T", "prod_UHhkcfAbNCbmC6"];

export function ShopGate({ children }: { children: React.ReactNode }) {
  const { user, loading, memberships, membershipsLoaded, refetchMemberships, setCurrentShop } = useAuth();
  const { t } = useTranslation("system");
  const { countryCode } = useLocale();
  // Plan prices: EUR for eurozone shops/visitors, USD otherwise (same as checkout).
  const { formatPlanPrice: usd } = useBillingMoney();
  const [isInviteRefetching, setIsInviteRefetching] = useState(false);
  const [subscriptionState, setSubscriptionState] = useState<{
    checked: boolean;
    hasSubscription: boolean;
    checkoutLoading: boolean;
    error: string | null;
    retryCount: number;
  }>({ checked: false, hasSubscription: false, checkoutLoading: false, error: null, retryCount: 0 });
  const [gateBillingInterval, setGateBillingInterval] = useState<"monthly" | "annual">("monthly");
  const [gatePromoCode, setGatePromoCode] = useState("");
  const [showGatePromo, setShowGatePromo] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [platformAdminChecked, setPlatformAdminChecked] = useState(false);
  const inviteRefetchAttempted = useRef(false);
  const pendingInviteShopId = user ? localStorage.getItem("pendingInviteShopId") : null;
  const pendingInviteToken = user ? localStorage.getItem("pendingInviteToken") : null;

  useEffect(() => {
    if (!user) {
      setPlatformAdminChecked(true);
      return;
    }
    supabase
      .from("platform_admins" as any)
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        setIsPlatformAdmin(!!data);
        setPlatformAdminChecked(true);
      });
  }, [user]);

  useEffect(() => {
    if (!user || loading || !membershipsLoaded || !platformAdminChecked) return;

    if (isPlatformAdmin) {
      setSubscriptionState({ checked: true, hasSubscription: true, checkoutLoading: false, error: null, retryCount: 0 });
      return;
    }

    const isOwner = memberships.some(m => m.role === "owner");
    const isSoloArtist = memberships.length === 0;

    if (!isOwner && !isSoloArtist) {
      setSubscriptionState({ checked: true, hasSubscription: true, checkoutLoading: false, error: null, retryCount: 0 });
      return;
    }

    // iOS short-circuit: if the user has an active `premium`
    // entitlement via Apple IAP (RevenueCat), they are subscribed
    // regardless of Stripe state. This also covers the gap before
    // the Phase 3 RevenueCat -> Supabase webhook lands: a fresh IAP
    // purchase grants entitlement on-device immediately, while the
    // Supabase user_subscriptions row gets written async by the
    // server-side webhook. Without this check, an iOS user who
    // just bought via IAP would be bounced to the paywall again
    // until the webhook caught up.
    if (isNativeIOS()) {
      const iosCheck = async () => {
        try {
          const isPremium = await hasPremiumEntitlement();
          if (isPremium) {
            setSubscriptionState({ checked: true, hasSubscription: true, checkoutLoading: false, error: null, retryCount: 0 });
            return true;
          }
        } catch {
          // Fall through to the Stripe-based check.
        }
        return false;
      };
      iosCheck().then((shortCircuited) => {
        if (shortCircuited) return;
        // No IAP entitlement — continue with the existing Stripe
        // checks below by re-bumping retryCount, which re-runs this
        // useEffect with the iOS branch already taken.
        // (We intentionally fall through rather than duplicating
        // the Stripe logic here — the same checks apply on iOS for
        // users who originally paid via web Stripe.)
      });
      // We do NOT return here; let the Stripe checks below run in
      // parallel. Whichever resolves first with hasSubscription=true
      // wins; if both come back false, we end up showing the paywall.
    }

    if (isSoloArtist) {
      const checkSoloSubscription = async () => {
        try {
          const { data, error } = await supabase.functions.invoke("check-subscription");
          if (error) throw error;
          const hasSoloSub = data?.subscriptions?.some(
            (sub: any) => SOLO_ARTIST_PRODUCT_IDS.includes(sub.productId)
          );
          setSubscriptionState({ checked: true, hasSubscription: !!hasSoloSub, checkoutLoading: false, error: null, retryCount: 0 });
        } catch {
          setSubscriptionState({ checked: true, hasSubscription: false, checkoutLoading: false, error: null, retryCount: 0 });
        }
      };
      checkSoloSubscription();
      return;
    }

    const checkSubscription = async () => {
      try {
        // For each shop where this user is an owner, the shop is "covered"
        // if the shop is still in trial OR ANY of its owners has an active
        // studio/solo subscription. The latter handles the co-owner case:
        // when one user (Luke) created the shop and pays the subscription,
        // and a second user (Lew) was promoted to owner via promote_to_owner,
        // Lew personally has no subscription but the SHOP does. Without this
        // check, Lew would be bounced to the upgrade modal even though the
        // shop is paid for.
        const ownerMemberships = memberships.filter(m => m.role === "owner");

        for (const om of ownerMemberships) {
          const { data: shop } = await supabase
            .from("shops")
            .select("trial_ends_at")
            .eq("id", om.shop_id)
            .single();

          if (shop?.trial_ends_at) {
            const trialEnd = new Date(shop.trial_ends_at);
            if (trialEnd > new Date()) {
              setSubscriptionState({ checked: true, hasSubscription: true, checkoutLoading: false, error: null, retryCount: 0 });
              return;
            }
          }

          const { data: shopHasSub, error: shopSubErr } = await supabase.rpc(
            "shop_has_active_studio_subscription" as any,
            { p_shop_id: om.shop_id } as any
          );
          if (!shopSubErr && shopHasSub === true) {
            setSubscriptionState({ checked: true, hasSubscription: true, checkoutLoading: false, error: null, retryCount: 0 });
            return;
          }
        }

        // Fall back to the user-scoped Stripe check. This matters for users
        // who own a shop but the user_subscriptions table is stale (the
        // edge function refreshes from Stripe live), or for the founding
        // owner who actually has the subscription on their account.
        const { data, error } = await supabase.functions.invoke("check-subscription");
        if (error) throw error;

        const hasStudio = data?.subscriptions?.some(
          (sub: any) => FIRST_STUDIO_PRODUCT_IDS.includes(sub.productId) || SOLO_ARTIST_PRODUCT_IDS.includes(sub.productId)
        );

        setSubscriptionState({ checked: true, hasSubscription: !!hasStudio, checkoutLoading: false, error: null, retryCount: 0 });
      } catch (e: any) {
        setSubscriptionState(prev => ({
          checked: true,
          hasSubscription: prev.retryCount >= 2,
          checkoutLoading: false,
          error: e?.message || "Unable to verify subscription",
          retryCount: prev.retryCount + 1,
        }));
      }
    };

    checkSubscription();
  }, [user, loading, membershipsLoaded, memberships, isPlatformAdmin, platformAdminChecked, subscriptionState.retryCount]);

  useEffect(() => {
    if (!user || loading || !membershipsLoaded) return;

    if (memberships.length > 0) {
      localStorage.removeItem("pendingInviteShopId");
      localStorage.removeItem("pendingInviteToken");
      inviteRefetchAttempted.current = false;
      return;
    }

    const pendingInviteShopId = localStorage.getItem("pendingInviteShopId");
    const pendingInviteToken = localStorage.getItem("pendingInviteToken");
    if (!pendingInviteShopId && !pendingInviteToken) return;
    if (inviteRefetchAttempted.current) return;

    inviteRefetchAttempted.current = true;
    setIsInviteRefetching(true);

    (async () => {
      await new Promise((r) => setTimeout(r, 400));

      if (pendingInviteToken) {
        const { data, error } = await (supabase.rpc as any)("accept_team_invitation", {
          _token: pendingInviteToken,
        });

        localStorage.removeItem("pendingInviteToken");

        if (!error) {
          const invitedShopId =
            typeof data === "string" && data.length > 0 ? data : pendingInviteShopId;
          if (invitedShopId) {
            localStorage.setItem("pendingInviteShopId", invitedShopId);
            setCurrentShop(invitedShopId);
          }
        } else {
          localStorage.removeItem("pendingInviteShopId");
        }
      }

      await refetchMemberships(user.id);
      setIsInviteRefetching(false);
    })();
  }, [user, loading, membershipsLoaded, memberships.length, refetchMemberships, setCurrentShop]);

  const handleCheckout = async (priceType: "first_studio" | "solo_artist") => {
    // App Store compliance — on native iOS we redirect users to manage
    // billing on the web instead of opening Stripe Checkout in-app. The
    // subscription state syncs back to the app via Supabase, so once they
    // pay on the web they hit the gate as fully subscribed on the next
    // session refresh.
    if (isNativeIOS()) {
      window.open("https://shopledger.app/settings", "_blank");
      return;
    }
    setSubscriptionState(prev => ({ ...prev, checkoutLoading: true }));
    try {
      const fnName = priceType === "solo_artist" ? "create-solo-artist-checkout" : "create-checkout";
      const body = priceType === "solo_artist"
        ? { billingInterval: gateBillingInterval, promoCode: gatePromoCode.trim() || undefined, country: countryCode }
        : { priceType: "first_studio", quantity: 1, billingInterval: gateBillingInterval, promoCode: gatePromoCode.trim() || undefined, country: countryCode };
      const { data, error } = await supabase.functions.invoke(fnName, { body });
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
    } catch (e: any) {
      const msg = e?.message || e?.context?.body?.error || t("errors.generic", { ns: "common" });
      alert(msg);
    }
    setSubscriptionState(prev => ({ ...prev, checkoutLoading: false }));
  };

  const handleRetrySubscriptionCheck = () => {
    setSubscriptionState(prev => ({ ...prev, checked: false, error: null }));
  };

  if (loading || (user && !membershipsLoaded) || isInviteRefetching || !subscriptionState.checked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (user && memberships.length === 0) {
    if ((pendingInviteShopId || pendingInviteToken) && !inviteRefetchAttempted.current) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      );
    }

    if (subscriptionState.hasSubscription) {
      return <>{children}</>;
    }

    localStorage.removeItem("pendingInviteShopId");
    localStorage.removeItem("pendingInviteToken");
    return <Navigate to="/create-shop" replace />;
  }

  if (user && !subscriptionState.hasSubscription) {
    const isOwner = memberships.some(m => m.role === "owner");
    if (isOwner) {
      // App Store compliance: on iOS, render the IAP paywall. The
      // paywall fetches Apple-localized prices from RevenueCat and
      // completes purchases via StoreKit (no non-IAP payment paths).
      // On a successful purchase or restore, onPurchaseSuccess
      // re-runs this gate, which short-circuits via
      // hasPremiumEntitlement() and lets the user through.
      if (isNativeIOS()) {
        return (
          <IOSPaywall
            onPurchaseSuccess={handleRetrySubscriptionCheck}
            onRetry={handleRetrySubscriptionCheck}
          />
        );
      }
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="w-full max-w-lg animate-fade-in">
            <div className="bg-card border border-border rounded-lg shadow-vintage p-8 text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <CreditCard className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-3xl text-foreground mb-2">{t("shopGate.trialEnded", { ns: "common" })}</h1>
                <p className="text-muted-foreground">
                  {t("shopGate.subscribeToContinue", { ns: "common" })}
                </p>
              </div>

              {subscriptionState.error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
                  <p>{t("shopGate.verifyTrouble", { ns: "common" })}</p>
                  <button
                    onClick={handleRetrySubscriptionCheck}
                    className="underline font-medium mt-1 hover:no-underline"
                  >
                    {t("shopGate.tryAgain", { ns: "common" })}
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2 p-1 bg-muted rounded-lg">
                <button
                  type="button"
                  onClick={() => setGateBillingInterval("monthly")}
                  className={`flex-1 text-sm py-1.5 px-3 rounded-md font-medium transition-colors ${
                    gateBillingInterval === "monthly"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t("shopGate.monthly")}
                </button>
                <button
                  type="button"
                  onClick={() => setGateBillingInterval("annual")}
                  className={`flex-1 text-sm py-1.5 px-3 rounded-md font-medium transition-colors ${
                    gateBillingInterval === "annual"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t("shopGate.annualSave")}
                </button>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleCheckout("solo_artist")}
                  disabled={subscriptionState.checkoutLoading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-3 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {subscriptionState.checkoutLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  {t("shopGate.soloArtistPro", {
                    ns: "common",
                    price: gateBillingInterval === "monthly"
                      ? t("shopGate.pricePerMonth", { ns: "common", amount: usd(19) })
                      : t("shopGate.priceAnnual", { ns: "common", monthly: usd(17.1), yearly: usd(205.2) }),
                  })}
                </button>
                <button
                  onClick={() => handleCheckout("first_studio")}
                  disabled={subscriptionState.checkoutLoading}
                  className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-md px-4 py-3 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {subscriptionState.checkoutLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  {t("shopGate.fullStudio", {
                    ns: "common",
                    price: gateBillingInterval === "monthly"
                      ? t("shopGate.pricePerMonth", { ns: "common", amount: usd(49) })
                      : t("shopGate.priceAnnual", { ns: "common", monthly: usd(44.1), yearly: usd(529.2) }),
                  })}
                </button>
                <p className="text-xs text-muted-foreground">
                  {t("shopGate.cancelAnytime", { ns: "common" })}
                </p>
              </div>

              <div>
                {!showGatePromo ? (
                  <button
                    type="button"
                    onClick={() => setShowGatePromo(true)}
                    className="text-sm text-primary hover:underline"
                  >
                    {t("shopGate.havePromoCode")}
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder={t("shopGate.promoPlaceholder")}
                      value={gatePromoCode}
                      onChange={(e) => setGatePromoCode(e.target.value.toUpperCase())}
                      className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      maxLength={50}
                    />
                    <button
                      type="button"
                      onClick={() => { setShowGatePromo(false); setGatePromoCode(""); }}
                      className="text-sm text-muted-foreground hover:text-foreground"
                    >
                      {t("shopGate.removePromo")}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={async () => {
                    await supabase.auth.signOut();
                    window.location.href = "/";
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground underline transition-colors"
                >
                  {t("shared.signOut")}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
